import React from 'react';
import { useTranslation } from 'react-i18next';
import {
    FormFieldInputNew,
    FormFieldSelect,
    FormFieldUploadFile,
    Layout,
} from '@netapp/bxp-design-system-react';
import { PROXY_TYPES } from 'modules/add-agent/components/common/ProxyConfiguration';
import { extractApiFailureMessage } from 'utils/api';
import { apiClient } from 'utils/clients';
import { configurations as i18nNamespace } from '../../../../i18n/namespace';
import styles from '../../style.module.scss';

export const PROXY_ACCESS_FIELDS = {
    PROXY_TYPE: 'proxyType',
    PROXY_PROTOCOL: 'proxyProtocol',
    PROXY_HOST: 'proxyHost',
    PROXY_PORT: 'proxyPort',
    PROXY_USER_NAME: 'proxyUserName',
    PROXY_PASSWORD: 'proxyPassword',
    CERTIFICATE_FILE: 'certificateFile',
};

export const getProxyTypeOptions = t => [
    { label: t('no_proxy'), value: PROXY_TYPES.NONE },
    { label: t('explicit_proxy'), value: PROXY_TYPES.EXPLICIT },
    { label: t('transparent_proxy'), value: PROXY_TYPES.TRANSPARENT },
];

export const parseProxyUri = uri => {
    if (!uri) {
        return { protocol: 'http', host: '', port: '' };
    }
    try {
        const parsed = new URL(uri.includes('://') ? uri : `http://${uri}`);
        return {
            protocol: parsed.protocol.replace(':', ''),
            host: parsed.hostname,
            port: parsed.port || (parsed.protocol === 'https:' ? '443' : '80'),
        };
    } catch (_e) {
        return { protocol: 'http', host: uri, port: '' };
    }
};

export const buildProxyUri = (host, port, protocol = 'http') => {
    if (!host) {
        return '';
    }
    const hostname = host.replace(/^(https?):\/\//i, '');
    return port ? `${protocol}://${hostname}:${port}` : `${protocol}://${hostname}`;
};

export const isProxyConfigurationComplete = formState => {
    const proxyType = formState[PROXY_ACCESS_FIELDS.PROXY_TYPE]?.value;
    if (!proxyType) {
        return false;
    }
    if (proxyType === PROXY_TYPES.NONE) {
        return true;
    }
    if (proxyType === PROXY_TYPES.TRANSPARENT) {
        return Boolean(formState[PROXY_ACCESS_FIELDS.CERTIFICATE_FILE]);
    }

    const port = formState[PROXY_ACCESS_FIELDS.PROXY_PORT];
    const portNumber = Number(port);
    const portIsValid = /^\d+$/.test(port) && portNumber >= 1 && portNumber <= 65535;
    const userName = formState[PROXY_ACCESS_FIELDS.PROXY_USER_NAME];
    const password = formState[PROXY_ACCESS_FIELDS.PROXY_PASSWORD];
    const credentialsComplete = (!userName && !password) || Boolean(userName && password);

    return Boolean(
        formState[PROXY_ACCESS_FIELDS.PROXY_HOST]
        && portIsValid
        && credentialsComplete
    );
};

export const getProxySubmitLabelKey = isConfigured => (isConfigured ? 'save' : 'configure');

export const isProxySubmitDisabled = (form, isConfigured) => {
    if (!isProxyConfigurationComplete(form?.formState)) {
        return true;
    }
    if (!isConfigured) {
        return false;
    }
    const hasChanges = Boolean(form?.isDirty) || Object.values(form?.dirty || {}).some(Boolean);
    return !hasChanges;
};

export const PROXY_API_HEADERS = {
    Accept: 'application/json',
};

export const PROXY_OPERATION_POLL_INTERVAL_MS = 5000;

// operation.status values of the state document. A change goes
// PRECHECK_IN_PROGRESS -> APPLY_IN_PROGRESS -> SUCCEEDED, and ends in
// PRECHECK_FAILED (the proxy in effect did not change) or FAILED (apply failed).
export const PROXY_OPERATION_STATUS = {
    PRECHECK_IN_PROGRESS: 'PRECHECK_IN_PROGRESS',
    PRECHECK_FAILED: 'PRECHECK_FAILED',
    APPLY_IN_PROGRESS: 'APPLY_IN_PROGRESS',
    SUCCEEDED: 'SUCCEEDED',
    FAILED: 'FAILED',
};

export const isFailedProxyStatus = status => (
    status === PROXY_OPERATION_STATUS.FAILED || status === PROXY_OPERATION_STATUS.PRECHECK_FAILED
);

const PENDING_PROXY_OPERATION_KEY = 'occm.console.pendingProxyOperation';

let pendingProxyOperationMemory = null;

export const markPendingProxyOperation = pendingOperation => {
    pendingProxyOperationMemory = pendingOperation;
    try {
        sessionStorage.setItem(PENDING_PROXY_OPERATION_KEY, JSON.stringify(pendingOperation));
    } catch (_error) {
        // Private browsing can reject sessionStorage. The in-memory copy still
        // resumes polling for this tab until a full reload.
    }
};

export const readPendingProxyOperation = () => {
    if (pendingProxyOperationMemory) {
        return pendingProxyOperationMemory;
    }
    try {
        const stored = sessionStorage.getItem(PENDING_PROXY_OPERATION_KEY);
        pendingProxyOperationMemory = stored ? JSON.parse(stored) : null;
    } catch (_error) {
        pendingProxyOperationMemory = null;
    }
    return pendingProxyOperationMemory;
};

export const clearPendingProxyOperation = () => {
    pendingProxyOperationMemory = null;
    try {
        sessionStorage.removeItem(PENDING_PROXY_OPERATION_KEY);
    } catch (_error) {
        // Nothing else can clear a storage write that the browser rejected.
    }
};

// The state document exposes a single `operation`. `operations` is accepted so
// a payload that names the same object in the plural still yields a message.
export const getProxyOperation = stateDocument => stateDocument?.operation || stateDocument?.operations;

// True when `operation` is an older operation than the one the last PUT or PATCH
// started (`expected` is the pending record: { jobId, createdAt }). The state
// document keeps the previous change's terminal operation until the new one is
// registered, so it must not be read as the outcome of the new request.
// Without a recorded jobId, or without timestamps to order the two, nothing is
// treated as stale, so polling can never be blocked by a record it cannot compare.
export const isStaleProxyOperation = (operation, expected) => {
    if (!operation || !expected?.jobId || operation.jobId === expected.jobId) {
        return false;
    }
    const operationTime = Date.parse(operation.createdAt);
    const expectedTime = Date.parse(expected.createdAt);
    if (Number.isNaN(operationTime) || Number.isNaN(expectedTime)) {
        return false;
    }
    return operationTime < expectedTime;
};

// True when `operation` carries what the stale check needs: a jobId, and a
// createdAt that parses as a date so the operations can be ordered.
export const canIdentifyProxyOperation = operation => (
    typeof operation?.jobId === 'string'
    && Boolean(operation.jobId.trim())
    && !Number.isNaN(Date.parse(operation.createdAt))
);

export const isTerminalProxyOperation = stateDocument => {
    const status = getProxyOperation(stateDocument)?.status;
    return status === PROXY_OPERATION_STATUS.SUCCEEDED || isFailedProxyStatus(status);
};

export const getProxyApiUrl = () => `${process.env.REACT_APP_OCCM_ENDPOINT}system/config/proxy`;

// Returns the latest operation of the last PUT or PATCH in `operation.status`.
export const getProxyStateApiUrl = () => `${getProxyApiUrl()}/state`;

export const fetchProxyState = async () => {
    const { data } = await apiClient.get(getProxyStateApiUrl(), { headers: PROXY_API_HEADERS });
    return data;
};

// The proxy settings (`proxy_type`, `proxy_url`, `ca_cert`, ...) live in
// `currentState.value` of the state document. The state GET is the only read:
// there is no separate GET on /system/config/proxy.
export const getProxyCurrentConfig = stateDocument => stateDocument?.currentState?.value || null;

// The failure reason is read from `operation.failure.message` only. Returns ''
// when the response has no such message so the caller shows its generic text.
export const getProxyStateErrorMessage = body => {
    const message = getProxyOperation(body)?.failure?.message;
    return typeof message === 'string' ? message.trim() : '';
};

// One state GET, reduced to what the pollers act on:
//  - { stateDocument }: the response has `operation.status`, so keep going by status.
//  - { errorMessage, currentConfig }: no `operation.status` could be found (the
//    response has no `operation`, or its `operation` has no status, or the API
//    answered with an error), so polling must stop and the failure is shown.
//    `errorMessage` is `operation.failure.message` or '' (the caller then shows its
//    generic text). `currentConfig` is `currentState.value` when the response had it.
//  - { retry: true }: no response at all (network error), so try again.
export const readProxyState = async () => {
    try {
        const stateDocument = await fetchProxyState();
        const status = getProxyOperation(stateDocument)?.status;
        if (typeof status === 'string' && status.trim()) {
            return { stateDocument };
        }
        return {
            errorMessage: getProxyStateErrorMessage(stateDocument),
            currentConfig: getProxyCurrentConfig(stateDocument),
        };
    } catch (error) {
        if (!error?.response) {
            return { retry: true };
        }
        return { errorMessage: getProxyStateErrorMessage(error.response.data) };
    }
};

// Used by the configuration page after a PUT or PATCH. Polls the state until the
// operation is applying (or already finished), which is when the page can close
// and the card takes over polling. Resolves with `errorMessage` when the
// operation failed or the state has no `operation`, so the page can show it.
export const waitForProxyApplyStart = ({
    isCancelled = () => false,
    interval = PROXY_OPERATION_POLL_INTERVAL_MS,
} = {}) => new Promise(resolve => {
    const check = async () => {
        if (isCancelled()) {
            resolve({ cancelled: true });
            return;
        }
        const result = await readProxyState();
        if (isCancelled()) {
            resolve({ cancelled: true });
            return;
        }
        if (result.stateDocument) {
            const operation = getProxyOperation(result.stateDocument);
            if (isStaleProxyOperation(operation, readPendingProxyOperation())) {
                // The previous change's operation: wait for the one just started.
                setTimeout(check, interval);
                return;
            }
            const { status } = operation;
            // PRECHECK_IN_PROGRESS keeps polling. PRECHECK_FAILED never reaches
            // APPLY_IN_PROGRESS, so it ends the wait the same way FAILED does.
            if (isFailedProxyStatus(status)) {
                resolve({
                    stateDocument: result.stateDocument,
                    status,
                    errorMessage: operation.failure?.message || '',
                });
                return;
            }
            if (
                status === PROXY_OPERATION_STATUS.APPLY_IN_PROGRESS
                || status === PROXY_OPERATION_STATUS.SUCCEEDED
            ) {
                resolve({ stateDocument: result.stateDocument, status });
                return;
            }
        } else if (!result.retry) {
            resolve({ status: PROXY_OPERATION_STATUS.FAILED, errorMessage: result.errorMessage });
            return;
        }
        setTimeout(check, interval);
    };
    check();
});

// A rejected request returns a problem document: `invalidParams[].reason` names
// the rejected field, and `description` explains the rest (409 conflict, PATCH
// without a stored proxy, ...). The specific reason wins. Anything else falls back
// to `extractApiFailureMessage`, whose literal "Unknown" is dropped so the caller
// can use its own message instead.
export const getProxyApiErrorMessage = error => {
    const data = error?.response?.data;
    const reason = data?.invalidParams?.[0]?.reason;
    if (typeof reason === 'string' && reason.trim()) {
        return reason.trim();
    }
    if (typeof data?.description === 'string' && data.description.trim()) {
        return data.description.trim();
    }
    const message = extractApiFailureMessage(error, 'detail');
    return message && message !== 'Unknown' ? message : '';
};

export const fetchProxyConfiguration = async ({ dispatch, t } = {}) => {
    try {
        return getProxyCurrentConfig(await fetchProxyState());
    } catch (error) {
        dispatch?.({
            type: 'NOTIFICATIONS:ERROR',
            payload: {
                message: t ? t('failed_to_fetch_proxy') : 'Failed to load the proxy configuration.',
                exception: error,
                moreInfo: getProxyApiErrorMessage(error) || undefined,
                key: 'GET-PROXY',
                showGlobal: true,
            },
        });
        return null;
    }
};

export const selectProxyType = proxyConfig => proxyConfig?.proxy_type?.toUpperCase();
export const selectProxyUrl = proxyConfig => {
    const proxyUrl = proxyConfig?.proxy_url;
    if (!proxyUrl) {
        return '';
    }
    return proxyUrl.replace(/^(https?):\/\//i, '');
};
// The state document never repeats the PEM: a stored CA is `ca_cert_b64` plus a
// parsed `certificate` info object (commonName, expiryDate, ...).
export const selectHasCertificate = proxyConfig => Boolean(
    proxyConfig?.ca_cert || proxyConfig?.ca_cert_b64 || proxyConfig?.certificate
);

// Value for the certificate upload field. A PEM string is used as is. A stored CA
// that the API only describes becomes an "existing certificate" marker: it counts
// as uploaded, but is not sent again, so a PATCH keeps the stored CA.
export const getProxyCertificateFormValue = proxyConfig => {
    if (typeof proxyConfig?.ca_cert === 'string' && proxyConfig.ca_cert) {
        return proxyConfig.ca_cert;
    }
    if (typeof proxyConfig?.certificate === 'string' && proxyConfig.certificate) {
        return proxyConfig.certificate;
    }
    return selectHasCertificate(proxyConfig) ? { isExistingCertificate: true } : '';
};

// A certificate is present when the field holds PEM text, an uploaded file with
// contents, or the marker for a CA the API already stores. Any other object (an
// empty upload value, file metadata without contents) is not a certificate.
export const hasProxyCertificate = certificateFile => {
    if (typeof certificateFile === 'string') {
        return Boolean(certificateFile.trim());
    }
    if (!certificateFile || typeof certificateFile !== 'object') {
        return false;
    }
    const { contents, isExistingCertificate } = certificateFile;
    return (typeof contents === 'string' && Boolean(contents.trim())) || isExistingCertificate === true;
};

export const getCertificateUploadPlaceholder = (certificateFile, t) => {
    return t(hasProxyCertificate(certificateFile)
        ? 'certificate_uploaded_replace'
        : 'choose_a_certificate');
};

export const buildProxyApiPayload = formState => {
    const payload = {};
    const proxyType = formState?.[PROXY_ACCESS_FIELDS.PROXY_TYPE]?.value;
    if (proxyType) {
        payload.proxy_type = proxyType;
    }

    const certificateFile = formState?.[PROXY_ACCESS_FIELDS.CERTIFICATE_FILE];
    // The scheme is always http. An uploaded certificate is the CA the backend trusts
    // for HTTPS traffic that the proxy decrypts. It does not mean the proxy port
    // itself speaks TLS, and an https:// URL makes the backend start a TLS handshake
    // that a plain HTTP proxy port rejects as an invalid request.
    const proxyUrl = buildProxyUri(
        formState?.[PROXY_ACCESS_FIELDS.PROXY_HOST],
        formState?.[PROXY_ACCESS_FIELDS.PROXY_PORT]
    );
    if (proxyUrl) {
        payload.proxy_url = proxyUrl;
    }

    const proxyUsername = formState?.[PROXY_ACCESS_FIELDS.PROXY_USER_NAME];
    if (proxyUsername) {
        payload.proxy_username = proxyUsername;
    }

    const proxyPassword = formState?.[PROXY_ACCESS_FIELDS.PROXY_PASSWORD];
    if (proxyPassword) {
        payload.proxy_password = proxyPassword;
    }

    const caCert = certificateFile?.contents || (typeof certificateFile === 'string' ? certificateFile : '');
    if (caCert) {
        payload.ca_cert = caCert;
    }

    return payload;
};

export const buildProxyApiPayloadFromConfig = proxyConfig => {
    if (!proxyConfig?.proxy_type) {
        return null;
    }
    const { protocol, host, port } = parseProxyUri(proxyConfig.proxy_url);
    return buildProxyApiPayload({
        [PROXY_ACCESS_FIELDS.PROXY_TYPE]: { value: proxyConfig.proxy_type?.toUpperCase() },
        [PROXY_ACCESS_FIELDS.PROXY_PROTOCOL]: protocol,
        [PROXY_ACCESS_FIELDS.PROXY_HOST]: host,
        [PROXY_ACCESS_FIELDS.PROXY_PORT]: port,
        [PROXY_ACCESS_FIELDS.PROXY_USER_NAME]: proxyConfig.proxy_username || '',
        [PROXY_ACCESS_FIELDS.PROXY_PASSWORD]: proxyConfig.proxy_password || '',
        [PROXY_ACCESS_FIELDS.CERTIFICATE_FILE]: getProxyCertificateFormValue(proxyConfig),
    });
};

export const requiresProxyTypeReset = (previousProxyType, nextProxyType) => {
    if (!previousProxyType || !nextProxyType || previousProxyType === nextProxyType) {
        return false;
    }
    const isSwitchableType = proxyType => (
        proxyType === PROXY_TYPES.EXPLICIT || proxyType === PROXY_TYPES.TRANSPARENT
    );
    return isSwitchableType(previousProxyType) && isSwitchableType(nextProxyType);
};

// An unconfigured proxy is created with PUT. Switching explicit <-> transparent
// is also a PUT so the new type replaces the current one in one request. Other
// updates to an existing proxy use PATCH.
// The request only accepts the change. The console card polls the state
// document and shows the success or failure notification once the operation
// reaches SUCCEEDED or FAILED.
export const submitProxyConfiguration = async ({
    formState,
    isConfigured,
    previousProxyType,
    t,
}) => {
    const payload = buildProxyApiPayload(formState);
    const nextProxyType = formState?.[PROXY_ACCESS_FIELDS.PROXY_TYPE]?.value;
    const url = getProxyApiUrl();
    const requestConfig = { headers: PROXY_API_HEADERS };
    const isTypeSwitch = requiresProxyTypeReset(previousProxyType, nextProxyType);
    const notificationKey = isConfigured && !isTypeSwitch ? 'PATCH-PROXY' : 'PUT-PROXY';

    try {
        const response = (!isConfigured || isTypeSwitch)
            ? await apiClient.put(url, payload, requestConfig)
            : await apiClient.patch(url, payload, requestConfig);
        // The response is the state document of the operation this request started
        // (202) or of the matching operation already in effect (200). Its jobId and
        // createdAt let the pollers ignore an older terminal operation that the
        // state may still carry until the new one is registered.
        const startedOperation = getProxyOperation(response?.data);
        if (!canIdentifyProxyOperation(startedOperation)) {
            // Without them the pollers could not tell the previous change's
            // SUCCEEDED or FAILED from the outcome of this one, and would report
            // the wrong result. The request itself was accepted, so nothing is
            // tracked and the user is told to check the status instead.
            return {
                success: false,
                errorMessage: t('proxy_request_not_tracked'),
            };
        }
        markPendingProxyOperation({
            successMessageKey: isConfigured ? 'proxy_updated_successfully' : 'proxy_configured_successfully',
            failureMessageKey: isConfigured ? 'failed_to_update_proxy' : 'failed_to_configure_proxy',
            notificationKey,
            jobId: startedOperation?.jobId,
            createdAt: startedOperation?.createdAt,
        });
        return { success: true };
    } catch (error) {
        return {
            success: false,
            errorMessage: getProxyApiErrorMessage(error)
                || t(isConfigured ? 'failed_to_update_proxy' : 'failed_to_configure_proxy'),
        };
    }
};

const ExplicitProxyFields = ({ form, t }) => (
    <>
        <Layout.GridItem lg={12}>
            <div className={styles.hostPortRow}>
                <div className={styles.hostField}>
                    <FormFieldInputNew
                        form={form}
                        name={PROXY_ACCESS_FIELDS.PROXY_HOST}
                        label={t('http_proxy')}
                    />
                </div>
                <span className={styles.hostPortSeparator}>:</span>
                <div className={styles.portField}>
                    <FormFieldInputNew
                        form={form}
                        name={PROXY_ACCESS_FIELDS.PROXY_PORT}
                        label={t('port')}
                    />
                </div>
            </div>
        </Layout.GridItem>
        <Layout.GridItem lg={12}>
            <FormFieldUploadFile
                form={form}
                name={PROXY_ACCESS_FIELDS.CERTIFICATE_FILE}
                label={t('upload_certificate')}
                placeholder={getCertificateUploadPlaceholder(form.formState[PROXY_ACCESS_FIELDS.CERTIFICATE_FILE], t)}
                isOptional={true}
            />
        </Layout.GridItem>
        <Layout.GridItem lg={6} md={6} sm={12}>
            <FormFieldInputNew
                form={form}
                name={PROXY_ACCESS_FIELDS.PROXY_USER_NAME}
                label={t('username')}
                placeholder={t('username_placeholder')}
                isOptional={true}
                autoComplete="off"
            />
        </Layout.GridItem>
        <Layout.GridItem lg={6} md={6} sm={12}>
            <FormFieldInputNew
                form={form}
                name={PROXY_ACCESS_FIELDS.PROXY_PASSWORD}
                label={t('password')}
                type="password"
                isOptional={true}
            />
        </Layout.GridItem>
    </>
);

const TransparentProxyFields = ({ form, t }) => (
    <Layout.GridItem lg={12}>
        <FormFieldUploadFile
            form={form}
            name={PROXY_ACCESS_FIELDS.CERTIFICATE_FILE}
            label={t('upload_certificate')}
            placeholder={getCertificateUploadPlaceholder(form.formState[PROXY_ACCESS_FIELDS.CERTIFICATE_FILE], t)}
        />
    </Layout.GridItem>
);

const ProxyConfiguration = ({
    form,
}) => {
    const { t } = useTranslation(i18nNamespace);
    const { formState } = form;
    const proxyType = formState[PROXY_ACCESS_FIELDS.PROXY_TYPE]?.value;
    const onProxyTypeChange = (value, event) => {
        form.handleFormChange(value, event);
        [
            PROXY_ACCESS_FIELDS.PROXY_HOST,
            PROXY_ACCESS_FIELDS.PROXY_PORT,
            PROXY_ACCESS_FIELDS.PROXY_USER_NAME,
            PROXY_ACCESS_FIELDS.PROXY_PASSWORD,
            PROXY_ACCESS_FIELDS.CERTIFICATE_FILE,
        ].forEach(name => {
            form.handleFormChange('', { name, ignoreDirty: true });
        });
    };

    return (
        <Layout.Grid rowGap="lg">
            <Layout.GridItem lg={6} md={6} sm={12}>
                <FormFieldSelect
                    form={form}
                    name={PROXY_ACCESS_FIELDS.PROXY_TYPE}
                    label={t('proxy_type')}
                    options={getProxyTypeOptions(t)}
                    onChange={onProxyTypeChange}
                />
            </Layout.GridItem>
            {proxyType === PROXY_TYPES.EXPLICIT && (
                <ExplicitProxyFields form={form} t={t} />
            )}
            {proxyType === PROXY_TYPES.TRANSPARENT && (
                <TransparentProxyFields form={form} t={t} />
            )}
        </Layout.Grid>
    );
};

export default React.memo(ProxyConfiguration);
